// src/pages/Dashboard/Admin/DetailsAdherents/components/AchatsPanel.jsx

import React, { useMemo } from "react";
import DashboardDataTable from "./DashboardDataTable";

import {
  calculateAchatsStats,
  formatCurrency,
  formatDate,
  getPaymentStatusClass,
  safeArray,
} from "../utils/adherentDashboardUtils";

function Badge({ children, className }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ${className}`}
    >
      {children}
    </span>
  );
}

function getTypeAchatLabel(type) {
  if (type === "licence-assurance-cotisation") return "Licence / Cotisation";
  if (type === "boutique") return "Boutique";
  return type || "—";
}

export default function AchatsPanel({ achats }) {
  const rows = useMemo(() => {
    return safeArray(achats).sort(
      (a, b) =>
        new Date(b?.date_achat || 0).getTime() -
        new Date(a?.date_achat || 0).getTime()
    );
  }, [achats]);

  const stats = useMemo(() => calculateAchatsStats(rows), [rows]);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em]">
            Total achats
          </p>
          <p className="mt-1 text-xl font-semibold text-slate-950 dark:text-white">
            {formatCurrency(stats.total)}
          </p>
        </div>

        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em]">
            Réglé
          </p>
          <p className="mt-1 text-xl font-semibold text-slate-950 dark:text-white">
            {formatCurrency(stats.regle)}
          </p>
        </div>

        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em]">
            Non réglé
          </p>
          <p className="mt-1 text-xl font-semibold text-slate-950 dark:text-white">
            {formatCurrency(stats.nonRegle)}
          </p>
        </div>
      </div>

      <DashboardDataTable
        minWidth="1050px"
        columns={[
          {
            key: "article",
            label: "Achat",
            render: (row) => (
              <div>
                <p className="font-semibold text-slate-900 dark:text-white">
                  {row?.Article?.nom ||
                    (row?.type_achat === "licence-assurance-cotisation"
                      ? "Licence • Assurance • Cotisation"
                      : "Achat")}
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {getTypeAchatLabel(row?.type_achat)}
                </p>
              </div>
            ),
          },
          {
            key: "date",
            label: "Date",
            render: (row) => formatDate(row?.date_achat),
          },
          {
            key: "user",
            label: "Gestionnaire",
            render: (row) =>
              row?.User
                ? `${row.User.prenom || ""} ${row.User.nom || ""}`.trim()
                : "—",
          },
          {
            key: "quantity",
            label: "Qté",
            render: (row) => row?.quantite || 1,
          },
          {
            key: "price",
            label: "Montant",
            render: (row) => (
              <span className="font-semibold text-slate-900 dark:text-white">
                {formatCurrency(row?.prix_total)}
              </span>
            ),
          },
          {
            key: "status",
            label: "Statut",
            render: (row) => (
              <Badge className={getPaymentStatusClass(row?.statut_reglement)}>
                {row?.statut_reglement || "—"}
              </Badge>
            ),
          },
          {
            key: "type_reglement",
            label: "Paiement",
            render: (row) => row?.type_reglement || "—",
          },
          {
            key: "message",
            label: "Message",
            render: (row) => row?.message_achat || "—",
          },
        ]}
        rows={rows}
        emptyMessage="Aucun achat trouvé pour les gestionnaires de cet adhérent."
      />
    </div>
  );
}